import React, { useRef, useEffect } from "react";
import cx from "classnames";
import ScaleText from "react-scale-text";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export default function GSFContent({
  testWords,
  correctWord,
  optionWords,
  hiddenOptionWords,
  isCorrect,
  isIncorrect,
  selectionWord,
  highlightedIndices,
  showAnswer,
  interactive,
  handleGuess,
  currentPuzzleEnded,
  isiPad
}) {

  const correctTargetRef = useRef();
  const allOptionsRef = useRef([]);

  useEffect(() => {
    if (showAnswer && correctTargetRef.current) {
      // Move the correct option to the correct target
      const targetRect = correctTargetRef.current.getBoundingClientRect();
      const targetX = targetRect.left;
      const targetY = targetRect.top;

      const correctOptionIndex = optionWords.findIndex(
        (wordData) => wordData.word === correctWord
      );

      if (correctOptionIndex > -1) {
        const correctOptionRef = allOptionsRef.current[correctOptionIndex];

        if (correctOptionRef) {
          const startingRect = correctOptionRef.getBoundingClientRect();
          const startingX = startingRect.left + window.scrollX;
          const startingY = startingRect.top + window.scrollY;

          const deltaX = targetX - startingX;
          const deltaY = targetY - startingY;

          correctOptionRef.style.transition = "transform 1.5s";
          correctOptionRef.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        }
      }
    } else {
      // Reset transition and transform of all options
      allOptionsRef.current.forEach((optionRef) => {
        optionRef.style.transition = "none";
        optionRef.style.transform = "none";
      });
    }
  }, [showAnswer]);

  function getDraggableStyle(style, snapshot) {
    if (!snapshot.isDropAnimating) {
      return style;
    }

    const { moveTo } = snapshot.dropAnimation;
    // move to the right spot
    const translate = `translate(${moveTo.x + 75}px, ${moveTo.y + 55}px)`;

    // patching the existing style
    return {
      ...style,
      transform: `${translate}`,
    };
  }

  const onDragEnd = (result) => {
    if (result && result.destination) {
      // result.destination will only be non null if the user dropped in the empty square
      const guessWord = result.draggableId;
      handleGuess(guessWord);
    }
  }

  const handleOptionClick = (guessWord) => {
    if (!interactive || !isiPad) {
      return;
    }

    handleGuess(guessWord);
  }

  function getMaxFontSize() {
    return window.innerWidth < 1150 ? 32 : 36;
  }

  const renderTestWord = (wordData, index) => {
    const isHiddenWord = correctWord === wordData.word;

    const quadrantClasses = cx({
      quadrant: true,
      "top-left": index === 0,
      "top-right": index === 1,
      "bottom-left": index === 2,
      "bottom-right": index === 3,
      highlighted: highlightedIndices.includes(index),
      correct: isHiddenWord && isCorrect,
      incorrect: isHiddenWord && isIncorrect,
    });

    const wordIsVisible =
      !isHiddenWord || // This is not the hidden word
      isCorrect || // There has been a correct guess
      isIncorrect; // There has been an incorrect guess

    let wordToShow = wordData.word;
    if (isHiddenWord && isIncorrect) {
      wordToShow = selectionWord;
    }

    const wordItemClasses = cx({
      "word-item": true,
      hidden: !wordIsVisible,
    });

    return (
      <Droppable
        key={index}
        isDropDisabled={!isHiddenWord || currentPuzzleEnded}
        droppableId={wordData.word}
      >
        {(provided, snapshot) => (
          <div
            className={quadrantClasses}
            key={index}
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              backgroundColor: snapshot.isDraggingOver ? "#f9dcbc" : undefined,
            }}
          >
            <div
              className={wordItemClasses}
              ref={isHiddenWord ? correctTargetRef : undefined}
            >
              <ScaleText widthOnly={true} maxFontSize={getMaxFontSize()}>
                <p>{wordToShow}</p>
              </ScaleText>
            </div>
            {!wordIsVisible && <p className="placeholder">?</p>}
          </div>
        )}
      </Droppable>
    );
  };

  const renderOption = (wordData, index) => {
    const isHidden = hiddenOptionWords.includes(wordData.word);
    const optionWordItemClasses = cx({
      "word-item": true,
      hidden: isHidden,
    });

    return (
      <Droppable
        droppableId={"options-" + wordData.word}
        isDropDisabled={true}
        key={index}
      >
        {(providedDroppable) => (
          <div
            className="option"
            ref={providedDroppable.innerRef}
            {...providedDroppable.droppableProps}
            onClick={() => handleOptionClick(wordData.word)}
          >
            <div ref={(el) => (allOptionsRef.current[index] = el)}>
              <Draggable
                key={wordData.word}
                draggableId={wordData.word}
                index={index}
                isDragDisabled={!interactive || isiPad}
              >
                {(provided, snapshot) => (
                  <DraggablePortalHandler snapshot={snapshot}>
                    <div
                      className={optionWordItemClasses}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      style={getDraggableStyle(
                        provided.draggableProps.style,
                        snapshot
                      )}
                    >
                      <ScaleText widthOnly={true} maxFontSize={getMaxFontSize()}>
                        <p>{wordData.word}</p>
                      </ScaleText>
                    </div>
                  </DraggablePortalHandler>
                )}
              </Draggable>
            </div>
          </div>
        )}
      </Droppable>
    );
  };

  return (
    <div className="gsf-container">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid-container">
          {testWords.map((wordData, index) => renderTestWord(wordData, index))}
        </div>
        <div className="options-container">
          {optionWords.map((wordData, index) => renderOption(wordData, index))}
        </div>
      </DragDropContext>
    </div>
  );
}

// fix drag offset in lexa-studentapp
function DraggablePortalHandler({ children, snapshot }) {
  if (snapshot.isDragging)
    return ReactDOM.createPortal(children, document.body);
  return children;
}
